/**
 * Canonical signed-in platform-language authority.
 *
 * Platform language belongs to the authenticated user account. Country and
 * BrandStore signals can constrain or suggest eligible choices, but they never
 * persist, select, or switch the preference.
 */

export const PLATFORM_LOCALE_METADATA_KEY = 'platform_locale' as const;
export const DEFAULT_PLATFORM_LOCALE = 'en-US' as const;

export const SUPPORTED_PLATFORM_LOCALES = ['en-US', 'pl-PL'] as const;
export type SupportedPlatformLocale = (typeof SUPPORTED_PLATFORM_LOCALES)[number];

export const POLAND_PLATFORM_LOCALES = [
  'pl-PL',
  'en-US',
] as const satisfies readonly SupportedPlatformLocale[];
const ENGLISH_ONLY_PLATFORM_LOCALES = [
  'en-US',
] as const satisfies readonly SupportedPlatformLocale[];

export interface PlatformLanguageEligibilityContext {
  /** Profile/member country signal. Eligibility only; never persistence. */
  profileCountryCode?: unknown;
  /** Relevant BrandStore countries. Eligibility only; never persistence. */
  brandStoreCountryCodes?: readonly unknown[];
  /** Existing or newly selected explicit account preference. */
  explicitLocale?: unknown;
}

function canonicalLocaleToken(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const token = value.trim().replace(/_/g, '-').toLowerCase();
  return token || null;
}

/** Parse only the currently supported canonical platform languages. */
export function parseSupportedPlatformLocale(value: unknown): SupportedPlatformLocale | null {
  switch (canonicalLocaleToken(value)) {
    case 'en-us':
      return 'en-US';
    case 'pl-pl':
      return 'pl-PL';
    default:
      return null;
  }
}

export function isSupportedPlatformLocale(value: unknown): value is SupportedPlatformLocale {
  return value === 'en-US' || value === 'pl-PL';
}

/** Missing, malformed, or unsupported values always resolve to canonical English. */
export function normalizePlatformLocale(value: unknown): SupportedPlatformLocale {
  return parseSupportedPlatformLocale(value) ?? DEFAULT_PLATFORM_LOCALE;
}

export function resolvePlatformLocaleFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): SupportedPlatformLocale {
  return normalizePlatformLocale(metadata?.[PLATFORM_LOCALE_METADATA_KEY]);
}

function normalizeCountryCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const token = value.trim().toUpperCase();
  return token || null;
}

export function isPolandPlatformLanguageContext(
  context: PlatformLanguageEligibilityContext,
): boolean {
  if (normalizeCountryCode(context.profileCountryCode) === 'PL') return true;
  return (context.brandStoreCountryCodes ?? []).some((code) => normalizeCountryCode(code) === 'PL');
}

/**
 * Current eligibility contract.
 *
 * Poland exposes exactly Polski and English. Outside a Poland signal, English
 * is the only newly offered language until another market contract is approved.
 * An already explicit Polish choice remains eligible when the user switches to
 * a non-Polish BrandStore.
 */
export function getEligiblePlatformLocales(
  context: PlatformLanguageEligibilityContext = {},
): readonly SupportedPlatformLocale[] {
  const explicitLocale = parseSupportedPlatformLocale(context.explicitLocale);
  if (isPolandPlatformLanguageContext(context) || explicitLocale === 'pl-PL') {
    return POLAND_PLATFORM_LOCALES;
  }
  return ENGLISH_ONLY_PLATFORM_LOCALES;
}

/** Create the canonical Supabase Auth metadata payload for an explicit choice. */
export function createPlatformLocaleMetadata(
  existingMetadata: Record<string, unknown> | null | undefined,
  locale: SupportedPlatformLocale,
): Record<string, unknown> {
  if (!isSupportedPlatformLocale(locale)) {
    throw new RangeError(`Unsupported platform locale: ${String(locale)}`);
  }

  return {
    ...(existingMetadata ?? {}),
    [PLATFORM_LOCALE_METADATA_KEY]: locale,
  };
}
